import { Field, Label } from "@headlessui/react";
import { clientErrorMessages } from "@lobby/i18n";
import { useTranslate } from "@lobby/ocb-intl";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useRef, useState } from "react";

import { Player } from "../../../../../entities";
import {
  APIError,
  Button,
  ClientError,
  Icon,
  Input,
  useErrorTranslate,
} from "../../../../../shared";

const passwordValidationError = {
  samePasswords: clientErrorMessages.SAME_PASSWORD_ERROR,
};

const THEME = (import.meta as any).env.VITE_THEME;

export function Authentication() {
  const playerResponse = Player.usePlayer();

  const isTwoFactorConfigured = playerResponse?.data?.isTwoFactorConfigured;

  if (THEME !== "bestspins365") {
    return <ChangePasswordForm />;
  }

  return (
    <>
      <ChangePasswordForm />
      {isTwoFactorConfigured ? (
        <DisableTwoFactorAuthentication />
      ) : (
        <EnableTwoFactorAuthentication />
      )}
    </>
  );
}

function ChangePasswordForm() {
  const { $t } = useTranslate();
  const { formatMessage } = useErrorTranslate();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [canUpdate, setCanUpdate] = useState(false);

  const updateMutation = Player.useChangePassword();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setValidationError("");

    const form = new FormData(e.currentTarget);

    if (validateForm(form)) {
      changePassword(form);
    }
  }

  function changePassword(form: FormData) {
    updateMutation.mutate(
      {
        oldPassword: form.get("old-password") as string,
        newPassword: form.get("new-password") as string,
      },
      {
        onSuccess: ({ error }) => {
          if (error) {
            const message = formatMessage(new APIError(error.message, { code: error.code }));
            setValidationError(message);
          } else {
            setValidationError("");
            setOldPassword("");
            setNewPassword("");
          }
        },
      },
    );
  }

  function validateForm(form: FormData) {
    if (!validatePasswords(form)) {
      const error = formatMessage(new ClientError(passwordValidationError.samePasswords));
      setValidationError(error);
      return false;
    }

    return true;
  }

  function validatePasswords(form: FormData) {
    return validatePasswordsSet(form) && validatePasswordsDiffer(form);
  }

  function validatePasswordsSet(form: FormData) {
    return Array.from(form.values()).every(Boolean);
  }

  function validatePasswordsDiffer(form: FormData) {
    return form.get("old-password") !== form.get("new-password");
  }

  function handleChange(e: FormEvent<HTMLFormElement>) {
    const form = new FormData(e.currentTarget);
    setCanUpdate(validatePasswordsSet(form));
  }

  return (
    <form onSubmit={handleSubmit} onChange={handleChange}>
      <Field className="flex flex-col">
        <Label>{$t({ defaultMessage: "Current Password" })}:</Label>
        <Input.Password
          className="w-full pe-13"
          name="old-password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          placeholder={$t({ defaultMessage: "Current Password" })}
          autoComplete="current-password"
        />
      </Field>
      <Field className="flex flex-col mt-5">
        <Label>{$t({ defaultMessage: "New Password" })}:</Label>
        <Input.Password
          className="w-full pe-13"
          name="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={$t({ defaultMessage: "New Password" })}
          autoComplete="new-password"
        />
        {validationError && (
          <div
            style={{
              color: "var(--error-accent-color)",
            }}
            className="text-12/5 lg:text-14/5"
          >
            {formatMessage(validationError)}
          </div>
        )}
      </Field>

      <div className="flex items-center gap-3 mt-6 mobile-only:justify-end">
        <Button
          variant="secondary"
          type="submit"
          loading={updateMutation.isPending}
          disabled={!canUpdate}
          className="mx-auto"
        >
          {$t({ defaultMessage: "Update" })}
        </Button>
      </div>
    </form>
  );
}

function EnableTwoFactorAuthentication() {
  const { $t } = useTranslate();
  const navigate = useNavigate();

  return (
    <div className="user-profile--two-factor-authentication-section">
      <svg width="1em" height="1em" fill="currentColor" viewBox="64 64 896 896">
        <path d="M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32M332 240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224H332zm460 600H232V536h560zM484 701v53c0 4.4 3.6 8 8 8h40c4.4 0 8-3.6 8-8v-53a48.01 48.01 0 1 0-56 0" />
      </svg>
      <h2>{$t({ defaultMessage: "Two-factor authentication is not enabled yet." })}</h2>
      <p>
        {$t({
          defaultMessage:
            "Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to sign in.",
        })}
      </p>
      <Button
        variant="primary"
        onClick={() => navigate({ to: "/two-factor/setup" })}
        className="mt-4 mx-auto [&_.truncate]:whitespace-normal"
      >
        {$t({ defaultMessage: "Enable two-factor authentication" })}
      </Button>
    </div>
  );
}

function DisableTwoFactorAuthentication() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { $t } = useTranslate();
  const navigate = useNavigate();

  return (
    <div className="user-profile--two-factor-authentication-section">
      <svg width="1em" height="1em" fill="currentColor" viewBox="64 64 896 896">
        <path d="M832 464h-68V240c0-70.7-57.3-128-128-128H388c-70.7 0-128 57.3-128 128v224h-68c-17.7 0-32 14.3-32 32v384c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V496c0-17.7-14.3-32-32-32M332 240c0-30.9 25.1-56 56-56h248c30.9 0 56 25.1 56 56v224H332zm460 600H232V536h560zM484 701v53c0 4.4 3.6 8 8 8h40c4.4 0 8-3.6 8-8v-53a48.01 48.01 0 1 0-56 0" />
      </svg>
      <h2>{$t({ defaultMessage: "Two-factor authentication enabled" })}</h2>
      <p>
        {$t({
          defaultMessage:
            "Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to sign in.",
        })}
      </p>
      <Button
        variant="danger"
        onClick={() => dialogRef.current?.showModal()}
        className="mt-4 mx-auto [&_.truncate]:whitespace-normal"
      >
        {$t({ defaultMessage: "Disable two-factor authentication" })}
      </Button>

      <dialog
        ref={dialogRef}
        className="rounded-[0.25rem] -animated bg-[var(--dialog-bg)] border-[var(--border)] border-1 text-start w-[40rem]"
      >
        <div className="flex justify-between font-semibold items-center gap-4 p-4 pb-0">
          <div>
            {$t({
              defaultMessage: "Are you sure you want to disable two-factor authentication?",
            })}
          </div>
          <Button variant="secondary" fit onClick={() => dialogRef.current?.close()}>
            <Icon className="text-[1rem]" name="close" />
          </Button>
        </div>
        <div className="p-4">
          <p>
            {$t({
              defaultMessage:
                "Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to sign in. We highly recommend that you keep two-factor authentication enabled on your account.",
            })}
          </p>
        </div>
        <hr className="text-[var(--border)]" />
        <div className="flex gap-4 justify-end p-4">
          <Button variant="secondary" onClick={() => dialogRef.current?.close()}>
            {$t({ defaultMessage: "Cancel" })}
          </Button>
          <Button variant="danger" onClick={() => navigate({ to: "/two-factor/disable" })}>
            {$t({ defaultMessage: "Disable" })}
          </Button>
        </div>
      </dialog>
    </div>
  );
}
